import React, { useEffect, useState, useRef } from "react";
import { makeStyles, Popper } from "@material-ui/core";
import clsx from "clsx";
import Downshift from "downshift";
import Paper from "@material-ui/core/Paper";
import { connect } from "react-redux";
import { remCalc } from "../../utils/helpers";
import poweredByGoogle from "../../assets/images/icons/powered-by-google.png";
import Box from "@material-ui/core/Box";
import RenderSuggestion from "../common/RenderSuggestion";
import Form from "../common/Form";

const useStyles = makeStyles(theme => ({
  container: {
    marginBottom: 4,
    flexGrow: 1,
    "& .MuiInputBase-input::placeholder": {
      color: "#808080",
      fontSize: "15px"
    }
  },
  paper: {
    left: 0,
    right: 0,
    zIndex: 1,
    overflow: "hidden",
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    boxShadow: `${theme.variables.boxShadow}, 0 -2px 4px rgba(0, 0, 0, 0.1)`,
    marginTop: 10
  },
  noMarginTop: {
    marginTop: "0 !important"
  },
  paperOnTop: {
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  suggestion: {
    textOverflow: "ellipsis",
    overflow: "hidden"
  },
  disabled: {
    pointerEvents: "all"
  }
}));

function VehicleAutocomplete({
  inputId,
  label,
  topLabel,
  dataCy,
  initialValue,
  helperText,
  error,
  shrinkLabel,
  handleSelect,
  mapBounds,
  width,
  disabled,
  placeholder,
  endLocation,
  commonElement,
  openPanelOnTab,
  currentVehicleSelection,
  isBackhaul,
  noMarginTop = false
}) {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [tempVal, setTempVal] = useState(initialValue || "");
  const [anchorEl, setAnchorEl] = useState(null);

  // Create session for autocomplete as suggests google's billing documentation
  const session = useRef(
    new window.google.maps.places.AutocompleteSessionToken()
  );

  const downshiftRef = useRef();
  const elRef = useRef();

  // Input timeout
  const handleInputChange = value => {
    setOpen(true);
    setTempVal(value || "");

    if (value) {
      const autocomplete = new window.google.maps.places.AutocompleteService();

      autocomplete.getPlacePredictions(
        {
          input: value,
          sessionToken: session.current,
          // Suggest addresses from map's current boundaries on window
          bounds: {
            north: mapBounds._northEast && mapBounds._northEast.lat,
            south: mapBounds._southWest && mapBounds._southWest.lat,
            west: mapBounds._southWest && mapBounds._southWest.lng,
            east: mapBounds._northEast && mapBounds._northEast.lng
          },
          types: ["address"]
        },
        function (results) {
          setSuggestions(results ? results : []);
        }
      );
    } else {
      setSuggestions([]);
      setTempVal("");
      handleSelect(null);
    }

    downshiftRef.current.setHighlightedIndex(0);
  };

  // Fix selected address
  const handleItemSelect = selectedItem => {
    setOpen(false);

    if (selectedItem) {
      setTempVal(selectedItem.description);
      handleSelect(selectedItem);
    }
  };

  const onInputFocus = e => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
    setTempVal(initialValue || "");

    e.target.select();
  };

  const handleKeyDown = (e, downshiftKeyDown) => {
    if (e.keyCode === 13) {
      downshiftRef.current.selectHighlightedItem();
    } else if (e.keyCode === 27) {
      anchorEl.blur();
    } else downshiftKeyDown(e);
  };

  const onInputBlur = () => {
    setOpen(false);
    setSuggestions([]);

    // Reset input value if nothing was selected
    setTempVal(initialValue || "");
  };

  useEffect(() => {
    setTempVal(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    if (!endLocation && !commonElement && !disabled) {
      setTimeout(
        () => elRef.current?.focus(),
        currentVehicleSelection?.length === 1 ? 500 : 100
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVehicleSelection]);

  return (
    <Downshift
      onInputValueChange={handleInputChange}
      onSelect={selectedItem => handleItemSelect(selectedItem)}
      defaultHighlightedIndex={0}
      itemToString={item => (item ? item.straat : "")}
      id="downshift-simple"
      ref={downshiftRef}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        inputValue,
        selectedItem
      }) => {
        const { onKeyDown } = getInputProps({
          placeholder: "Search for an address"
        });

        return (
          <div
            className={clsx(classes.container, {
              [classes.disabled]: disabled
            })}
          >
            {/* Form */}
            <Form
              data={[
                [
                  {
                    label,
                    topLabel,
                    dataCy,
                    id: inputId,
                    value: tempVal,
                    error: error,
                    helperText: helperText,
                    autoComplete: "off",
                    renderInputField: true,
                    shrink: shrinkLabel,
                    inputRef: elRef,
                    placeholder,
                    onBlur: e => onInputBlur(e),
                    onFocus: e => onInputFocus(e),
                    onKeyDown: e => {
                      handleKeyDown(e, onKeyDown);
                      endLocation && openPanelOnTab(e, 1);
                    },
                    onChange: e => handleInputChange(e.target.value),
                    disabled
                  }
                ]
              ]}
            />
            <Popper
              open={isOpen}
              anchorEl={anchorEl}
              style={{ zIndex: 100000 }}
            >
              <div style={{ width: width ? width : remCalc(331) }}>
                {isOpen && suggestions.length ? (
                  <Paper
                    className={clsx(classes.paper, {
                      [classes.paperOnTop]: isBackhaul,
                      [classes.noMarginTop]: noMarginTop
                    })}
                    square
                  >
                    {suggestions.map((suggestion, index) =>
                      RenderSuggestion(
                        {
                          suggestion,
                          index,
                          value: tempVal,
                          itemProps: getItemProps({ item: suggestion }),
                          iconHidden: !commonElement,
                          highlightedIndex,
                          selectedItem
                        },
                        true
                      )
                    )}

                    {/* Powered by Google */}
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      pt={1}
                      pb={1.5}
                      px={1.5}
                    >
                      <img
                        src={poweredByGoogle}
                        height={14}
                        alt="Powered by Google"
                      />
                    </Box>
                  </Paper>
                ) : null}
              </div>
            </Popper>
          </div>
        );
      }}
    </Downshift>
  );
}

function mapStateToProps(state) {
  return {
    mapBounds: state.map.bounds
  };
}

export default connect(mapStateToProps)(VehicleAutocomplete);
